import React, { useState, useEffect } from 'react';
import { getUsers, updateUserAccess, getCurrentUser } from './api';
import PageHeader from './ui/PageHeader';
import Spinner from './ui/Spinner';
import Alert from './ui/Alert';
import Badge from './ui/Badge';
import EmptyState from './ui/EmptyState';

/**
 * Team access table. Rendered inline on Settings and as the /users route page.
 */
export const UsersPanel = () => {
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, meData] = await Promise.all([getUsers(), getCurrentUser()]);
      setUsers(usersData);
      setCurrentUser(meData);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAccess = async (uid, currentAllowed) => {
    setUpdating(uid);
    try {
      await updateUserAccess(uid, !currentAllowed);
      setUsers(users.map(u => u.uid === uid ? { ...u, allowed: !currentAllowed } : u));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user access');
    } finally {
      setUpdating(null);
    }
  };

  const isAdmin = currentUser?.is_admin;

  if (loading) {
    return <Spinner label="Loading users…" />;
  }

  return (
    <div>
      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>
      {!isAdmin && (
        <p className="muted" style={{ margin: '0 0 12px' }}>
          Only the administrator can modify user access.
        </p>
      )}
      {users.length === 0 ? (
        <EmptyState title="No users yet" message="No one has signed in to the console." />
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last login</th>
                {isAdmin && <th className="actions">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.uid}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {user.photo_url ? (
                        <img
                          src={user.photo_url}
                          alt=""
                          style={{ width: '32px', height: '32px', borderRadius: '50%' }}
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div style={{
                          width: '32px', height: '32px', borderRadius: '50%',
                          backgroundColor: 'var(--color-muted)', color: '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '14px', fontWeight: 'bold', flexShrink: 0,
                        }}>
                          {(user.name || user.email || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <span>{user.name || '—'}</span>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    {user.is_admin ? <Badge state="info">Admin</Badge> : <Badge>User</Badge>}
                  </td>
                  <td>
                    <Badge state={user.allowed ? 'success' : 'danger'}>
                      {user.allowed ? 'Allowed' : 'Denied'}
                    </Badge>
                  </td>
                  <td className="muted small">
                    {user.last_login ? new Date(user.last_login).toLocaleString() : '—'}
                  </td>
                  {isAdmin && (
                    <td className="actions">
                      {user.is_admin ? (
                        <span className="muted small">—</span>
                      ) : (
                        <button
                          type="button"
                          className={`btn btn-sm ${user.allowed ? 'btn-danger' : 'btn-primary'}`}
                          onClick={() => handleToggleAccess(user.uid, user.allowed)}
                          disabled={updating === user.uid}
                        >
                          {updating === user.uid ? 'Saving…' : user.allowed ? 'Revoke access' : 'Grant access'}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Users = () => (
  <div className="page page-narrow">
    <PageHeader title="Team access" subtitle="Who can sign in to the console" />
    <UsersPanel />
  </div>
);

export default Users;
